import React, { useState } from 'react';
import { BookOpenIcon, PlayCircleIcon, CheckCircleIcon, ClockIcon, AwardIcon, SearchIcon, ChevronRightIcon, LockIcon, StarIcon } from 'lucide-react';
interface Course {
  id: number;
  title: string;
  category: string;
  duration: string;
  progress: number;
  completed: boolean;
  required: boolean;
  dueDate?: string;
  instructor: string;
  description: string;
  locked?: boolean;
}
const courses: Course[] = [{
  id: 1,
  title: 'TRID Compliance 2025',
  category: 'Compliance',
  duration: '30 min',
  progress: 0,
  completed: false,
  required: true,
  dueDate: 'Mar 10',
  instructor: 'Jennifer Walsh',
  description: 'Updated TRID disclosure requirements effective March 15, 2025.'
}, {
  id: 2,
  title: 'NMLS Annual Renewal',
  category: 'Licensing',
  duration: '8 hrs',
  progress: 45,
  completed: false,
  required: true,
  dueDate: 'Apr 30',
  instructor: 'Compliance Team',
  description: 'Complete your annual NMLS continuing education requirements.'
}, {
  id: 3,
  title: 'Encompass LOS Mastery',
  category: 'Systems',
  duration: '2 hrs',
  progress: 100,
  completed: true,
  required: false,
  instructor: 'IT Department',
  description: 'Advanced workflows, shortcuts, and best practices in Encompass.'
}, {
  id: 4,
  title: 'FHA & VA Product Training',
  category: 'Products',
  duration: '1.5 hrs',
  progress: 70,
  completed: false,
  required: false,
  instructor: 'Mark Chen',
  description: 'Deep dive into government loan products, guidelines, and pricing.'
}, {
  id: 5,
  title: 'Sales Conversion Techniques',
  category: 'Sales',
  duration: '45 min',
  progress: 100,
  completed: true,
  required: false,
  instructor: 'Carlos Mendez',
  description: 'Proven strategies for converting leads and closing more loans.'
}, {
  id: 6,
  title: 'Anti-Money Laundering (AML)',
  category: 'Compliance',
  duration: '1 hr',
  progress: 0,
  completed: false,
  required: true,
  dueDate: 'Jun 30',
  instructor: 'Compliance Team',
  description: 'Annual AML training required for all licensed mortgage professionals.'
}, {
  id: 7,
  title: 'Jumbo Loan Guidelines',
  category: 'Products',
  duration: '1 hr',
  progress: 0,
  completed: false,
  required: false,
  instructor: 'Secondary Markets',
  description: 'Non-conforming loan requirements, pricing, and underwriting criteria.',
  locked: true
}, {
  id: 8,
  title: 'Customer Experience Excellence',
  category: 'Sales',
  duration: '30 min',
  progress: 20,
  completed: false,
  required: false,
  instructor: 'Lisa Martinez',
  description: 'Building lasting client relationships and generating referrals.'
}];
const categories = ['All', 'Compliance', 'Licensing', 'Products', 'Sales', 'Systems'];
const categoryColors: Record<string, string> = {
  Compliance: '#EF4444',
  Licensing: '#2B8DC4',
  Products: '#1E6FA0',
  Sales: '#1A2B3C',
  Systems: '#2B8DC4'
};
export function LearningCenter() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTab, setActiveTab] = useState<'all' | 'required' | 'inprogress' | 'completed'>('all');
  const filtered = courses.filter(c => {
    const matchCat = activeCategory === 'All' || c.category === activeCategory;
    const matchSearch = c.title.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase());
    const matchTab = activeTab === 'all' || activeTab === 'required' && c.required || activeTab === 'inprogress' && c.progress > 0 && !c.completed || activeTab === 'completed' && c.completed;
    return matchCat && matchSearch && matchTab;
  });
  const completedCount = courses.filter(c => c.completed).length;
  const requiredPending = courses.filter(c => c.required && !c.completed).length;
  const totalHours = courses.filter(c => c.completed).reduce((acc, c) => acc + parseFloat(c.duration), 0);
  return <div style={{
    maxWidth: 1280,
    margin: '0 auto'
  }} data-id="element-364">
      {/* Header */}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 24,
      flexWrap: 'wrap',
      gap: 12
    }} data-id="element-365">
        <div data-id="element-366">
          <h2 style={{
          fontSize: 26,
          fontWeight: 900,
          color: '#1A2B3C',
          margin: '0 0 3px',
          fontStyle: 'italic',
          letterSpacing: '-0.04em'
        }} data-id="element-367">
            Learning Center
          </h2>
          <p style={{
          fontSize: 13,
          color: '#5A6A8A',
          margin: 0
        }} data-id="element-368">
            Training, certifications, and continuing education
          </p>
        </div>
        <div style={{
        position: 'relative'
      }} data-id="element-369">
          <SearchIcon size={14} style={{
          position: 'absolute',
          left: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#A0AECB'
        }} data-id="element-370" />
          <input type="text" placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)} style={{
          paddingLeft: 30,
          paddingRight: 12,
          paddingTop: 8,
          paddingBottom: 8,
          fontSize: 13,
          borderRadius: 8,
          border: '1.5px solid #E4EAF6',
          background: '#F4F8FC',
          color: '#1A2B3C',
          outline: 'none',
          width: 220
        }} onFocus={e => {
          e.target.style.borderColor = '#2B8DC4';
        }} onBlur={e => {
          e.target.style.borderColor = '#E4EAF6';
        }} data-id="element-371" />
        </div>
      </div>

      {/* Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: 16,
      marginBottom: 24
    }} data-id="element-372">
        {[{
        label: 'Courses Completed',
        value: `${completedCount}/${courses.length}`,
        color: '#2B8DC4',
        sub: 'this year'
      }, {
        label: 'Required Pending',
        value: String(requiredPending),
        color: '#EF4444',
        sub: 'need attention'
      }, {
        label: 'Hours Earned',
        value: `${totalHours.toFixed(0)}h`,
        color: '#2B8DC4',
        sub: 'CE credits'
      }, {
        label: 'Completion Rate',
        value: `${Math.round(completedCount / courses.length * 100)}%`,
        color: '#1A2B3C',
        sub: 'overall'
      }].map((stat, i) => <div key={i} style={{
        background: '#FFFFFF',
        borderRadius: 12,
        padding: '18px 20px',
        border: '1px solid #E4EAF6',
        borderTopWidth: 3,
        borderTopColor: stat.color,
        boxShadow: '0 2px 8px rgba(13,27,42,0.06)'
      }} data-id="element-373">
            <div style={{
          fontSize: 32,
          fontWeight: 900,
          color: '#1A2B3C',
          fontStyle: 'italic',
          letterSpacing: '-0.04em',
          lineHeight: 1,
          marginBottom: 4,
          position: 'relative',
          display: 'inline-block'
        }} data-id="element-374">
              {stat.value}
              <div style={{
            position: 'absolute',
            bottom: -3,
            left: 0,
            width: '60%',
            height: 3,
            borderRadius: 2,
            background: stat.color
          }} data-id="element-375" />
            </div>
            <div style={{
          fontSize: 12,
          color: '#5A6A8A',
          fontWeight: 600,
          marginTop: 8
        }} data-id="element-376">
              {stat.label}
            </div>
            <div style={{
          fontSize: 11,
          color: '#A0AECB',
          marginTop: 2
        }} data-id="element-377">
              {stat.sub}
            </div>
          </div>)}
      </div>

      {/* Filters Row */}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
      flexWrap: 'wrap',
      gap: 10
    }} data-id="element-378">
        <div style={{
        display: 'flex',
        gap: 6
      }} data-id="element-379">
          {(['all', 'required', 'inprogress', 'completed'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} style={{
          padding: '6px 14px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 800,
          fontStyle: 'italic',
          cursor: 'pointer',
          border: 'none',
          transition: 'all 0.15s',
          background: activeTab === tab ? '#2B8DC4' : '#FFFFFF',
          color: activeTab === tab ? '#FFFFFF' : '#5A6A8A',
          boxShadow: activeTab === tab ? '0 2px 8px rgba(43,141,196,0.3)' : '0 1px 3px rgba(13,27,42,0.08)'
        }} data-id="element-380">
                {tab === 'all' ? 'All' : tab === 'inprogress' ? 'In Progress' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>)}
        </div>
        <div style={{
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap'
      }} data-id="element-381">
          {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} style={{
          padding: '5px 12px',
          borderRadius: 5,
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          border: 'none',
          transition: 'all 0.15s',
          background: activeCategory === cat ? '#1A2B3C' : '#F4F8FC',
          color: activeCategory === cat ? '#FFFFFF' : '#5A6A8A'
        }} data-id="element-382">
              {cat}
            </button>)}
        </div>
      </div>

      {/* Course Grid */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 14
    }} data-id="element-383">
        {filtered.map(course => {
        const catColor = categoryColors[course.category] || '#2B8DC4';
        return <div key={course.id} style={{
          background: '#FFFFFF',
          borderRadius: 12,
          padding: '18px 20px',
          border: '1px solid #E4EAF6',
          borderTopWidth: 3,
          borderTopColor: course.completed ? '#2B8DC4' : course.required ? '#EF4444' : '#1A2B3C',
          boxShadow: '0 2px 8px rgba(13,27,42,0.06)',
          opacity: course.locked ? 0.6 : 1,
          transition: 'all 0.15s'
        }} onMouseEnter={e => {
          if (!course.locked) (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(43,141,196,0.12)';
        }} onMouseLeave={e => {
          ;
          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(13,27,42,0.06)';
        }} data-id="element-384">
              <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 10
          }} data-id="element-385">
                <div style={{
              flex: 1,
              minWidth: 0
            }} data-id="element-386">
                  <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 4,
                flexWrap: 'wrap'
              }} data-id="element-387">
                    <span style={{
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '2px 7px',
                  borderRadius: 3,
                  background: `${catColor}15`,
                  color: catColor,
                  letterSpacing: '0.04em'
                }} data-id="element-388">
                      {course.category}
                    </span>
                    {course.required && !course.completed && <span style={{
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '2px 7px',
                  borderRadius: 3,
                  background: '#FEF2F2',
                  color: '#EF4444',
                  letterSpacing: '0.04em'
                }} data-id="element-389">
                        REQUIRED
                      </span>}
                    {course.completed && <span style={{
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '2px 7px',
                  borderRadius: 3,
                  background: '#EBF5FB',
                  color: '#2B8DC4',
                  letterSpacing: '0.04em'
                }} data-id="element-390">
                        ✓ DONE
                      </span>}
                    {course.locked && <LockIcon size={11} color="#A0AECB" data-id="element-391" />}
                  </div>
                  <div style={{
                fontSize: 14,
                fontWeight: 900,
                color: '#1A2B3C',
                fontStyle: 'italic',
                letterSpacing: '-0.02em',
                lineHeight: 1.3,
                marginBottom: 4
              }} data-id="element-392">
                    {course.title}
                  </div>
                  <div style={{
                fontSize: 12,
                color: '#5A6A8A',
                lineHeight: 1.5,
                marginBottom: 8
              }} data-id="element-393">
                    {course.description}
                  </div>
                </div>
              </div>

              <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
            flexWrap: 'wrap'
          }} data-id="element-394">
                <span style={{
              fontSize: 11,
              color: '#A0AECB',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }} data-id="element-395">
                  <ClockIcon size={11} data-id="element-396" /> {course.duration}
                </span>
                <span style={{
              fontSize: 11,
              color: '#A0AECB'
            }} data-id="element-397">
                  by {course.instructor}
                </span>
                {course.dueDate && <span style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#EF4444'
            }} data-id="element-398">
                    Due {course.dueDate}
                  </span>}
              </div>

              {/* Progress Bar */}
              {!course.locked && <div style={{
            marginBottom: 12
          }} data-id="element-399">
                  <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 4
            }} data-id="element-400">
                    <span style={{
                fontSize: 11,
                color: '#A0AECB'
              }} data-id="element-401">
                      {course.completed ? 'Completed' : course.progress > 0 ? 'In progress' : 'Not started'}
                    </span>
                    <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#2B8DC4'
              }} data-id="element-402">
                      {course.progress}%
                    </span>
                  </div>
                  <div style={{
              height: 5,
              background: '#E4EAF6',
              borderRadius: 3,
              overflow: 'hidden'
            }} data-id="element-403">
                    <div style={{
                height: '100%',
                width: `${course.progress}%`,
                background: course.completed ? '#2B8DC4' : 'linear-gradient(90deg, #2B8DC4, #3FA8E0)',
                borderRadius: 3,
                transition: 'width 0.3s ease'
              }} data-id="element-404" />
                  </div>
                </div>}

              <button disabled={!!course.locked} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 700,
            cursor: course.locked ? 'not-allowed' : 'pointer',
            border: 'none',
            transition: 'all 0.15s',
            background: course.completed ? '#EBF5FB' : '#2B8DC4',
            color: course.completed ? '#2B8DC4' : 'white'
          }} data-id="element-405">
                {course.locked ? <>
                    <LockIcon size={12} data-id="element-406" /> Locked
                  </> : course.completed ? <>
                    <AwardIcon size={12} data-id="element-407" /> Review
                  </> : course.progress > 0 ? <>
                    <PlayCircleIcon size={12} data-id="element-408" /> Continue
                  </> : <>
                    <PlayCircleIcon size={12} data-id="element-409" /> Start Course
                  </>}
              </button>
            </div>;
      })}
      </div>

      {filtered.length === 0 && <div style={{
      textAlign: 'center',
      padding: '48px 0'
    }} data-id="element-410">
          <BookOpenIcon size={44} color="#C8D8E8" style={{
        marginBottom: 12
      }} data-id="element-411" />
          <p style={{
        fontWeight: 700,
        color: '#5A6A8A',
        margin: '0 0 4px'
      }} data-id="element-412">
            No courses found
          </p>
          <p style={{
        fontSize: 13,
        color: '#A0AECB',
        margin: 0
      }} data-id="element-413">
            Try adjusting your search or filter
          </p>
        </div>}
    </div>;
}